import { Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { ColleagueExpertResponse } from '@/api/types'
import { createLogger } from '@/utils/logger'

const logger = createLogger('ColleagueSuggestion')

interface ColleagueSuggestionProps {
  experts: ColleagueExpertResponse[]
}

/**
 * Displays colleague expertise suggestions as interactive chips.
 *
 * Shows structured colleague data from the expert_search tool
 * as amber chips similar to citations, prominently displayed after the message.
 *
 * Data comes from backend's colleague_suggestions field, populated when
 * Claude uses the expert_search tool.
 */
export function ColleagueSuggestion({ experts }: ColleagueSuggestionProps) {
  const { t } = useTranslation()

  if (!experts || experts.length === 0) return null

  return (
    <div className="mt-3 pt-3 border-t border-neutral-200/30">
      <div className="flex items-center gap-1.5 mb-2 text-sm text-warning-600">
        <Lightbulb className="w-3.5 h-3.5" />
        <span className="font-medium">{t('colleague.title')}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {experts.map((expert, index) => (
          <button
            key={index}
            type="button"
            className={cn(
              "group flex flex-col gap-1 p-2.5 rounded-lg",
              "bg-warning-50 border border-warning-200",
              "hover:bg-warning-100 hover:border-warning-300",
              "transition-colors duration-150",
              "min-h-[44px]" // iOS tap target
            )}
            onClick={() => {
              // Future: Open sheet with colleague details
              logger.debug('Colleague tapped:', expert.name)
            }}
            aria-label={t('colleague.detailsAriaLabel', { name: expert.name })}
          >
            <div className="flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-warning-600 flex-shrink-0" />
              <span className="font-medium text-neutral-900 text-sm">{expert.name}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-warning-100 text-warning-800 border border-warning-200">
                {expert.specialty}
              </span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-warning-100 text-warning-800 border border-warning-200">
                {t('colleague.yearsAbbrev', { years: expert.years_experience })}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// REMOVED: parseColleagueSuggestions - now using structured data from backend
// colleague_suggestions field instead of parsing from text
